
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useCountry } from '@/contexts/country';
import { useCountrySelection } from '@/hooks/useCountrySelection';
import CountryFlag from './CountryFlag';
import { Globe } from 'lucide-react';
import { hasLaunchDatePassed } from '@/utils/date-constants';

interface CountrySelectionDialogProps {
  detectedCountry?: {
    code: string;
    name: string;
  };
  forceOpen?: boolean;
}

const CountrySelectionDialog = ({ 
  detectedCountry,
  forceOpen = false 
}: CountrySelectionDialogProps) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  // Pass false to avoid showing dialog automatically
  const { showDialog, setShowDialog, saveSelection } = useCountrySelection(false);
  const { availableCountries } = useCountry();
  
  // Effect to handle dialog showing based on explicit control
  useEffect(() => {
    if (showDialog || forceOpen) {
      setOpen(true);
    }
  }, [showDialog, forceOpen]);

  const handleCountrySelect = (countryCode: string) => {
    saveSelection(countryCode);
    setOpen(false);
    setShowDialog(false);
    
    // Remove special handling for Cayman Islands
    // Now all countries will be handled the same way - no automatic redirects
    // Users need to click explicitly on navigation links to go to country pages
  };

  // Allow manual closing of the dialog
  const handleCloseDialog = () => {
    setOpen(false);
    setShowDialog(false);
  };

  if (!open) return null;

  const activeCountries = availableCountries.filter(country => country.active);

  return (
    <Dialog open={open} onOpenChange={handleCloseDialog}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Select Your Country
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {detectedCountry && (
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-sm text-muted-foreground">
                We detected that you're browsing from <strong>{detectedCountry.name}</strong>, 
                which is not yet available on our platform. Please select from one of our 
                available countries below:
              </p>
            </div>
          )}
          
          <div className="grid gap-4">
            {activeCountries.map((country) => (
              <Button
                key={country.code}
                variant="outline"
                className="flex items-center justify-start gap-2 h-12"
                onClick={() => handleCountrySelect(country.code)}
              >
                <CountryFlag countryCode={country.code} />
                <span>{country.name}</span>
              </Button>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => handleCountrySelect('global')} variant="outline">
            View Global
          </Button>
          <Button onClick={handleCloseDialog} variant="secondary">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { CountrySelectionDialog };
